import { Component, ChangeDetectionStrategy, signal, WritableSignal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { User } from "../../entity/User";
import { ActivatedRoute, Router } from "@angular/router";
import { ClientFilter } from "../../obj/ClientFilter";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { Company } from "../../entity/Company";

@Component({
    selector: "client-selector-dialog",
    templateUrl: "../../../html/incs/client-selector-dialog.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    // FormsModule is no longer needed because we use manual bindings
})
export class ClientSelectorDialog extends AbstractBuyerComponent {
    public confirmMessage: string | null = null;
    public confirmTitle: string = "Confirm";

    query: WritableSignal<string> = signal('');
    showResidential: WritableSignal<boolean> = signal(false);
    showCommercial: WritableSignal<boolean> = signal(false);
    searchAutocompleteItems: WritableSignal<ClientFilter[]> = signal([]);
    autocompleteCurrentPosition: WritableSignal<number> = signal(0);
    isVisibleAutocomplete: WritableSignal<boolean> = signal(true);

    user: User = new User();
    client: User | null = null;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public dialogRef: MatDialogRef<ClientSelectorDialog>,
        dialog: MatDialog
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.generalService.getDetails()
            .then(u => {
                this.user = u;
                if (this.user == null || !this.user.isAdvisor) {
                    this.dialogRef.close(null!);
                }
            });
    }

    // Handlers for checkbox toggles
    toggleResidential(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.showResidential.set(checked);
        this.searchSiteInputChanged(event);
        this.refreshView();
    }

    toggleCommercial(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.showCommercial.set(checked);
        this.searchSiteInputChanged(event);
        this.refreshView();
    }

    // Handler for text input
    onQueryInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.query.set(value);
        this.searchSiteInputChanged(event);
    }

    changeClient(clientFilter: ClientFilter): void {
        this.generalService.getClient(clientFilter.clientId)
            .then((res: any) => {
                this.client = res;
                if (this.client != null) {
                    if (clientFilter.hasMultipleCompanies) {
                        if (clientFilter.isResidential) {
                            this.generalService.switchStoredUserFilter(false, null);
                        } else {
                            var company = new Company();
                            company.id = clientFilter.companyId;
                            company.name = clientFilter.clientName;
                            this.generalService.switchStoredUserFilter(true, company);
                        }
                    } else {
                        this.generalService.clearStoredUserFilter();
                    }
                    this.generalService.switchStoredClient(this.client);
                }
                this.dialogRef.close(null!);
            });
    }

    searchFocus(): void {
        this.isVisibleAutocomplete.set(true);
    }

    searchFocusOut(): void {
        // Keep original behavior (no change)
    }

    searchSiteByKey(event: any): void {
        if (event.keyCode == 13) {
            const items = this.searchAutocompleteItems();
            const currentPos = this.autocompleteCurrentPosition();
            if (items && items[currentPos - 1]) {
                this.changeClient(items[currentPos - 1]);
            }
        }
        if (event.keyCode == 38) { // up
            const itemsLength = this.searchAutocompleteItems().length;
            if (itemsLength > 0) {
                let newPos = this.autocompleteCurrentPosition() - 1;
                if (newPos <= 0) {
                    newPos = itemsLength;
                }
                this.autocompleteCurrentPosition.set(newPos);
            }
        }
        if (event.keyCode == 40) { // down
            const itemsLength = this.searchAutocompleteItems().length;
            if (itemsLength > 0) {
                let newPos = this.autocompleteCurrentPosition() + 1;
                if (newPos > itemsLength) {
                    newPos = 1;
                }
                this.autocompleteCurrentPosition.set(newPos);
            }
        }
    }

    searchSiteInputChanged(event: any): void {
        this.isVisibleAutocomplete.set(true);
        let queryValue = this.query();
        if (queryValue == null) {
            queryValue = "";
            this.query.set("");
        }
        let residentialFlag = this.showResidential();
        let commercialFlag = this.showCommercial();
        if (!residentialFlag && !commercialFlag && queryValue.length > 0) {
            residentialFlag = true;
            commercialFlag = true;
            this.showResidential.set(true);
            this.showCommercial.set(true);
        }
        this.generalService.quickSearchClients(residentialFlag, commercialFlag, queryValue)
            .then((res: any) => {
                this.autocompleteCurrentPosition.set(0);
                this.searchAutocompleteItems.set(res);
            });
    }

    getSearchAutocompleteClass(): string {
        const visible = this.isVisibleAutocomplete() && this.searchAutocompleteItems().length > 0;
        return "clients-autocomplete " + (visible ? "visible" : "hidden");
    }

    getAutocompleteClassItem(pos: number): string {
        if (pos == this.autocompleteCurrentPosition() - 1) {
            return "selected";
        }
        return "";
    }

    addClient(): void {
        this.dialogRef.close(null!);
        this.router.navigate(["/client/add"]);
    }
}